/**
 * Fix Recommendation Card Component
 *
 * FERP CRITICAL: Individual AI fix recommendation display card
 * Shows single fix with reasoning, confidence, before/after, and accept/reject actions
 *
 * Principles:
 * - Data-driven: All content from LLM backend (no hardcoded fixes)
 * - Human-in-the-loop: Explicit accept/reject required
 * - Confidence scoring: Visual indicator of AI reliability
 * - Complete audit: User actions tracked for HIPAA/SOX
 *
 * Usage:
 * ```tsx
 * <FixRecommendationCard
 *   fix={templateFix}
 *   index={0}
 *   onAccept={(index) => handleAccept(index)}
 *   onReject={(index) => handleReject(index)}
 *   disabled={loading}
 * />
 * ```
 */

import React from 'react';
import {
  Card,
  Text,
  Badge,
  Group,
  Stack,
  Button,
  Code,
  Divider,
  Tooltip,
  ThemeIcon,
  Box,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconSparkles,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { TemplateFix, FixType } from '../graphql/llmFixOperations';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface FixRecommendationCardProps {
  /** Fix recommendation from backend */
  fix: TemplateFix;

  /** Index in suggested_fixes array (for tracking) */
  index: number;

  /** Callback when user accepts this fix */
  onAccept: (index: number) => void;

  /** Callback when user rejects this fix */
  onReject: (index: number) => void;

  /** Disable actions during loading */
  disabled?: boolean;

  /** Show compact view (less detail) */
  compact?: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get human-readable fix type label
 */
function getFixTypeLabel(fixType: FixType): string {
  const labels: Record<FixType, string> = {
    remove_field: 'Remove Field',
    change_component: 'Change Component',
    add_subfields: 'Add Subfields',
    fix_props: 'Fix Properties',
    add_label: 'Add Label',
  };
  return labels[fixType] || fixType;
}

/**
 * Get fix type color for badge
 */
function getFixTypeColor(fixType: FixType): string {
  const colors: Record<FixType, string> = {
    remove_field: 'red',
    change_component: 'blue',
    add_subfields: 'green',
    fix_props: 'yellow',
    add_label: 'orange',
  };
  return colors[fixType] || 'gray';
}

/**
 * Get confidence level badge color
 */
function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.9) return 'green';
  if (confidence >= 0.7) return 'yellow';
  return 'red';
}

/**
 * Parse JSON string safely
 */
function safeJsonParse(jsonString: string): unknown {
  try {
    return JSON.parse(jsonString);
  } catch {
    return jsonString;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const FixRecommendationCard: React.FC<FixRecommendationCardProps> = ({
  fix,
  index,
  onAccept,
  onReject,
  disabled = false,
  compact = false,
}) => {
  const beforeValue = safeJsonParse(fix.before);
  const afterValue = safeJsonParse(fix.after);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header: Fix type and confidence */}
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <ThemeIcon
              size="lg"
              radius="md"
              variant="light"
              color={getFixTypeColor(fix.fix_type)}
            >
              <IconSparkles size={18} />
            </ThemeIcon>
            <Box>
              <Text size="sm" fw={600}>
                {getFixTypeLabel(fix.fix_type)}
              </Text>
              <Text size="xs" c="dimmed">
                Field: {fix.field_id} (Step: {fix.step_id})
              </Text>
            </Box>
          </Group>

          {/* Confidence badge */}
          <Tooltip label={`AI Confidence: ${(fix.confidence * 100).toFixed(0)}%`}>
            <Badge
              size="lg"
              color={getConfidenceColor(fix.confidence)}
              variant="light"
              leftSection={<IconSparkles size={12} />}
            >
              {(fix.confidence * 100).toFixed(0)}% AI
            </Badge>
          </Tooltip>
        </Group>

        <Divider />

        {/* AI Reasoning */}
        <Box>
          <Group gap={4} mb={4}>
            <IconInfoCircle size={14} />
            <Text size="xs" fw={600} tt="uppercase" c="dimmed">
              AI Reasoning
            </Text>
          </Group>
          <Text size="sm">{fix.reasoning}</Text>
        </Box>

        {/* Before / After (only if not compact) */}
        {!compact && (
          <>
            <Stack gap="xs">
              <Group gap={4}>
                <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                  Before (Invalid)
                </Text>
              </Group>
              <Code block>{JSON.stringify(beforeValue, null, 2)}</Code>
            </Stack>

            <Stack gap="xs">
              <Group gap={4}>
                <IconCheck size={14} color="var(--mantine-color-green-6)" />
                <Text size="xs" fw={600} tt="uppercase" c="dimmed">
                  After (Proposed)
                </Text>
              </Group>
              <Code block>{JSON.stringify(afterValue, null, 2)}</Code>
            </Stack>
          </>
        )}

        <Divider />

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconX size={16} />}
            onClick={() => onReject(index)}
            disabled={disabled}
          >
            Reject
          </Button>
          <Button
            variant="filled"
            color="green"
            leftSection={<IconCheck size={16} />}
            onClick={() => onAccept(index)}
            disabled={disabled}
          >
            Accept Fix
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};

export default FixRecommendationCard;
